import {
  type ApolloError,
  isNetworkRequestSettled,
  type OperationVariables,
  type QueryRef,
  useReadQuery,
} from '@apollo/client';
import { type ReactElement, useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { RouteQueryBoundary } from './RouteQueryBoundary';
import { retainRouteQueryRef } from './routePreload';

export type RouteQueryRead<TData> = Readonly<{
  data: TData | undefined;
  error: ApolloError | undefined;
  /** The ref's request has finished (with data or an error); cached data can arrive before it. */
  settled: boolean;
}>;

type ReadReporter<TData> = (read: RouteQueryRead<TData>) => void;

function RouteQueryReader<TData, TVariables extends OperationVariables>({
  queryRef,
  onRead,
}: {
  queryRef: QueryRef<TData, TVariables>;
  onRead: ReadReporter<TData>;
}) {
  const { data, error, networkStatus } = useReadQuery(queryRef);
  useEffect(() => retainRouteQueryRef(queryRef), [queryRef]);
  useLayoutEffect(() => {
    onRead({ data, error, settled: isNetworkRequestSettled(networkStatus) });
  }, [data, error, networkStatus, onRead]);
  return null;
}

function RouteQueryReadError<TData>({ error, onRead }: { error: ApolloError; onRead: ReadReporter<TData> }) {
  useLayoutEffect(() => {
    onRead({ data: undefined, error, settled: true });
  }, [error, onRead]);
  return null;
}

const UNREAD = { data: undefined, error: undefined, settled: false } as const;

/**
 * Reads a route query ref for a page whose loading and error views live inside an already-mounted
 * tree (a grid overlay, a header picker). The returned `reader` renders nothing: it reads the ref with
 * `useReadQuery` below a `RouteQueryBoundary` and reports each result in the same commit, so the page
 * itself never suspends or remounts. Render `reader` once beside the content.
 */
export function useRouteQueryRead<TData, TVariables extends OperationVariables>(
  queryRef: QueryRef<TData, TVariables>
): { read: RouteQueryRead<TData>; reader: ReactElement };
/** With no ref there is nothing to read: `read` stays unsettled and `reader` is null. */
export function useRouteQueryRead<TData, TVariables extends OperationVariables>(
  queryRef: QueryRef<TData, TVariables> | undefined
): { read: RouteQueryRead<TData>; reader: ReactElement | null };
export function useRouteQueryRead<TData, TVariables extends OperationVariables>(
  queryRef: QueryRef<TData, TVariables> | undefined
): { read: RouteQueryRead<TData>; reader: ReactElement | null } {
  const [reported, setReported] = useState<{ queryRef: object; read: RouteQueryRead<TData> } | undefined>();
  const onRead = useCallback(
    (read: RouteQueryRead<TData>) => {
      if (queryRef !== undefined) setReported({ queryRef, read });
    },
    [queryRef]
  );
  // A read belongs to its ref: a new ref (a loader re-run) starts unread rather than showing the old one.
  const read = reported !== undefined && reported.queryRef === queryRef ? reported.read : UNREAD;
  if (queryRef === undefined) return { read, reader: null };
  const reader = (
    <RouteQueryBoundary
      queryRef={queryRef}
      fallback={null}
      errorFallback={({ error }) => <RouteQueryReadError error={error} onRead={onRead} />}
    >
      <RouteQueryReader queryRef={queryRef} onRead={onRead} />
    </RouteQueryBoundary>
  );
  return { read, reader };
}
